<template>
  <section class="dp-layout">
    <section class="dp-left">
      <DepartmentPane directory-scope="school" @tree-node-click="treeNodeClick" :type="1"></DepartmentPane>
    </section>

    <section class="dp-right">
      <toolbar :buttonList="buttonList" @callFunction="callFunction" :searchList="searchList"></toolbar>

      <!-- 业务说明：固定金额模式终端优先按个人配置扣款。最后更新：2026-09-15 -->
      <div class="biz-tip">
        <i class="el-icon-info"></i>
        <div class="biz-tip__body">
          <strong>业务说明：</strong>
          为学生配置个人固定消费金额。在消费模式为「固定金额模式」的终端上刷卡时，扣款优先级为：
          <em>本页个人配置 → 终端固定金额 → 终端上报金额</em>。
        </div>
      </div>

      <div class="tbl-scroll-host">
        <el-table
          :data="users"
          v-loading="listLoading"
          @select="dialogCheck"
          @row-click="selectCurrentRow"
          ref="table"
          class="custom-tbl"
          height="100%"
          style="width: 100%">
          <el-table-column type="selection" width="50" />
          <el-table-column prop="StuName" label="学生姓名" />
          <el-table-column prop="DepartmentName" label="部门" />
          <el-table-column prop="FixedAmount" label="固定金额（元）" />
          <el-table-column prop="Remark" label="备注" />
          <el-table-column prop="CreateTime" label="创建时间" :formatter="formatCreateTime" />
        </el-table>
      </div>

      <el-col :span="24" class="toolbar">
        <el-pagination
          layout="total, prev, pager, next, jumper"
          @current-change="handleCurrentChange"
          :page-size="50"
          :total="total"
          style="float: right" />
      </el-col>

      <!-- 新增 -->
      <el-dialog
        title="新增固定金额人员信息"
        :visible.sync="addFormVisible"
        :close-on-click-modal="false">
        <el-form :model="addForm" label-width="130px" :rules="addFormRules" ref="addForm">
          <el-form-item label="人员" required>
            <div class="ps-pick-summary">
              <span>
                已选 <strong>{{ personScopeCheckedCount }}</strong> 人
                <template v-if="personScopeDeptCount">（{{ personScopeDeptCount }} 个部门）</template>
              </span>
              <el-button type="primary" size="small" plain @click="openPersonScopeDialog">选择人员</el-button>
              <el-button
                v-if="personScopeCheckedCount"
                type="text"
                size="small"
                @click="clearPersonScopeSelection">
                清空
              </el-button>
            </div>
            <div v-if="personScopeCheckedCount" class="ps-pick-tags">
              <el-tag
                v-for="item in personScopeSelectedPreview"
                :key="item.value"
                size="mini"
                closable
                disable-transitions
                @close="removePersonScopeSelected(item.value)">
                {{ item.label }}
              </el-tag>
              <span v-if="personScopeCheckedCount > 8" class="ps-pick-more">等 {{ personScopeCheckedCount }} 人</span>
            </div>
          </el-form-item>

          <el-form-item label="固定金额（元）" prop="FixedAmount">
            <el-input-number
              v-model="addForm.FixedAmount"
              :min="1"
              :step="1"
              :precision="0"
              placeholder="请输入固定金额" />
          </el-form-item>

          <el-form-item label="备注">
            <el-input type="textarea" v-model="addForm.Remark" :rows="3" />
          </el-form-item>
        </el-form>
        <div slot="footer" class="dialog-footer">
          <el-button @click.native="addFormVisible = false">取消</el-button>
          <el-button type="primary" @click.native="addSubmit" :loading="addLoading">提交</el-button>
        </div>
      </el-dialog>

      <!-- 选人（仅学生，本业务不支持教职工）。最后更新：2026-09-15 -->
      <el-dialog
        title="选择人员"
        :visible.sync="personScopeVisible"
        :close-on-click-modal="false"
        append-to-body
        destroy-on-close
        width="1100px"
        top="6vh"
        custom-class="person-scope-dialog"
        @close="onPersonScopeDialogClose">
        <div class="ps-layout" v-loading="personScopeTreeLoading">
          <div class="ps-left">
            <div class="ps-left-header">
              <i class="el-icon-reading"></i>
              <span>家校通讯录</span>
            </div>
            <div class="ps-left-search">
              <el-input
                v-model="personScopeDeptFilter"
                clearable
                size="small"
                prefix-icon="el-icon-search"
                placeholder="输入关键字搜索机构/班级..." />
            </div>
            <div class="ps-left-tree">
              <el-tree
                ref="personScopeTeachingTree"
                :data="personScopeTeachingTree"
                node-key="value"
                highlight-current
                :expand-on-click-node="false"
                :props="personScopeTreeProps"
                :filter-node-method="filterPersonScopeDeptNode"
                :default-expanded-keys="personScopeTeachingExpandedKeys"
                @node-click="onPersonScopeDeptClick">
                <span class="ps-tree-node" slot-scope="{ node, data }">
                  <span class="ps-tree-node__dot" :class="'tone-' + personScopeNodeTone(data)"></span>
                  <span class="ps-tree-node__label" :title="node.label">{{ node.label }}</span>
                </span>
              </el-tree>
              <div v-if="!personScopeTreeLoading && !personScopeTeachingTree.length" class="ps-empty">暂无部门</div>
            </div>
          </div>

          <div class="ps-right">
            <el-alert
              title="本功能仅支持学生；固定金额模式终端扣款优先取本页个人配置"
              type="info"
              show-icon
              :closable="false"
              class="ps-alert" />

            <div class="ps-meta">
              已选人员：<strong>{{ personScopeCheckedCount }}</strong> 人
              <span class="ps-meta-sep">·</span>
              涉及部门：<strong>{{ personScopeDeptCount }}</strong> 个
            </div>

            <div class="ps-filter-bar">
              <el-input
                v-model="personScopeNameFilter"
                clearable
                size="small"
                prefix-icon="el-icon-search"
                placeholder="姓名"
                class="ps-name-input"
                @keyup.enter.native="loadPersonScopePeople" />
              <el-button type="primary" size="small" :disabled="!personScopeActiveDeptId" @click="loadPersonScopePeople">
                查询
              </el-button>
              <el-button
                size="small"
                :disabled="!personScopeActiveDeptId || !personScopeTableRows.length"
                @click="selectAllPersonScopeCurrentDept">
                全选当前部门
              </el-button>
              <el-button size="small" :disabled="!personScopeActiveDeptId" @click="clearPersonScopeCurrentDept">
                清空当前部门
              </el-button>
              <el-button size="small" type="text" :disabled="!personScopeCheckedCount" @click="clearPersonScopeSelection">
                清空全部已选
              </el-button>
            </div>

            <div class="ps-hint" v-if="personScopeActiveDeptId">
              当前部门：<strong>{{ personScopeActiveDeptName }}</strong>
              <span class="ps-hint-sub">（{{ personScopeTableRows.length }} 人）</span>
            </div>
            <div class="ps-hint ps-hint--warn" v-else>请先在左侧选择部门</div>

            <div class="ps-table-wrap" ref="personScopeTableWrap" v-loading="personScopeTableLoading">
              <el-table
                ref="personScopeTable"
                :data="personScopePagedRows"
                row-key="value"
                size="small"
                stripe
                highlight-current-row
                :height="personScopeTableHeight"
                @row-click="onPersonScopeRowClick"
                @selection-change="onPersonScopeTableSelectionChange">
                <el-table-column type="selection" width="46" align="center" />
                <el-table-column prop="label" label="姓名" min-width="120" show-overflow-tooltip />
                <el-table-column prop="deptLabel" label="部门" min-width="140" show-overflow-tooltip />
                <el-table-column label="头像" width="72" align="center" header-align="center">
                  <template slot-scope="scope">
                    <oss-media-thumbs
                      v-if="scope.row.HeadPhotoImage"
                      :media="scope.row.HeadPhotoImage"
                      thumb-width="40px"
                      thumb-height="40px" />
                    <span v-else class="ps-avatar-empty">无</span>
                  </template>
                </el-table-column>
              </el-table>
            </div>

            <div class="ps-pager-wrap">
              <el-pagination
                layout="total, prev, pager, next, jumper"
                :page-size="personScopePageSize"
                :total="personScopeTableRows.length"
                :current-page="personScopePage"
                @current-change="handlePersonScopePageChange" />
            </div>

            <div class="ps-actions">
              <el-button @click="personScopeVisible = false">取消</el-button>
              <el-button type="primary" @click="confirmPersonScopeSelection">
                确定（{{ personScopeCheckedCount }}）
              </el-button>
            </div>
          </div>
        </div>
      </el-dialog>
    </section>
  </section>
</template>

<script>
import util from '../../../util/date';
import { testapi } from '../../api/modules/auth';
import {
  getFixedAmountUserManageListPage,
  addFixedAmountUserManage,
  deleteFixedAmountUserManage,
} from '../../api/modules/consumption';
import { getTeachingDepartmentTree } from '../../api/modules/department';
import { getStudentSelectList } from '../../api/modules/personnel';
import {
  lockPersonScopeSelectionSync,
  syncPersonScopeElTableSelection,
} from '../../utils/personScopeTableSelection';
import { getButtonList } from '../../promissionRouter';
import Toolbar from '../../components/Toolbar';
import DepartmentPane from '../../components/DepartmentPane';

export default {
  components: { Toolbar, DepartmentPane },
  data() {
    return {
      filters: {},
      searchList: [{ label: '名称', key: 'name' }],
      users: [],
      total: 0,
      buttonList: [],
      currentRow: null,
      page: 1,
      listLoading: false,
      addFormVisible: false,
      addLoading: false,
      addFormRules: {
        FixedAmount: [
          { required: true, message: '请输入固定金额', trigger: 'blur' },
          { type: 'number', min: 1, message: '固定金额必须为大于0的正整数', trigger: 'blur' },
        ],
      },
      addForm: {
        Remark: '',
        DepartmentId: 0,
        FixedAmount: 1,
        UserIds: [],
      },
      DepartmentId: 0,

      personScopeVisible: false,
      personScopeTreeLoading: false,
      personScopeTableLoading: false,
      personScopeDeptFilter: '',
      personScopeNameFilter: '',
      personScopeTeachingTree: [],
      personScopeTeachingExpandedKeys: [],
      personScopeTreeProps: { label: 'label', children: 'children' },
      personScopeActiveDeptId: '',
      personScopeActiveDeptName: '',
      personScopeTableRows: [],
      personScopePage: 1,
      personScopePageSize: 10,
      personScopeTableHeight: 400,
      personScopeSelectedMap: {},
      personScopeCheckedCount: 0,
      personScopeDeptCount: 0,
      personScopeSyncingTable: false,
    };
  },
  computed: {
    personScopePagedRows() {
      const rows = this.personScopeTableRows || [];
      const size = this.personScopePageSize || 10;
      const page = this.personScopePage || 1;
      return rows.slice((page - 1) * size, page * size);
    },
    personScopeSelectedPreview() {
      return Object.keys(this.personScopeSelectedMap || {})
        .map((k) => this.personScopeSelectedMap[k])
        .slice(0, 8);
    },
  },
  watch: {
    personScopeDeptFilter(val) {
      const tree = this.$refs.personScopeTeachingTree;
      if (tree) tree.filter(val);
    },
  },
  beforeDestroy() {
    this.unbindPersonScopeResize();
  },
  methods: {
    formatCreateTime(row) {
      return !row.CreateTime
        ? ''
        : util.formatDate.format(new Date(row.CreateTime), 'yyyy-MM-dd hh:mm:ss');
    },
    treeNodeClick(data) {
      this.DepartmentId = data.value;
      this.handleQuery();
    },
    dialogCheck(selection, row) {
      this.currentRow = null;
      this.$refs.table.clearSelection();
      if (!selection.length) return;
      if (row) this.selectCurrentRow(row);
    },
    selectCurrentRow(val) {
      if (!val) return;
      this.currentRow = val;
      this.$refs.table.clearSelection();
      this.$refs.table.toggleRowSelection(val, true);
    },
    callFunction(item) {
      if (typeof item.search == 'object') this.filters = item.search;
      else this.filters = { key: item.search };
      this[item.Func].apply(this, item);
    },
    handleCurrentChange(val) {
      this.page = val;
      this.handleQuery();
    },
    handleQuery() {
      this.listLoading = true;
      testapi();
      getFixedAmountUserManageListPage({
        page: this.page,
        intPageSize: 50,
        departmentId: this.DepartmentId,
        ...this.filters,
      }).then((res) => {
        this.total = res.data.response.dataCount;
        this.users = res.data.response.data;
        this.listLoading = false;
      });
    },
    handleDel() {
      const row = this.currentRow;
      if (!row) {
        this.$message({ message: '请选择要删除的一行数据！', type: 'error' });
        return;
      }
      this.$confirm('确认删除该记录吗?', '提示', { type: 'warning' })
        .then(() => {
          this.listLoading = true;
          deleteFixedAmountUserManage({ id: row.Id }).then((res) => {
            this.listLoading = false;
            if (util.isEmt.format(res)) return;
            this.$message({
              message: res.data.success ? '删除成功' : res.data.msg,
              type: res.data.success ? 'success' : 'error',
            });
            this.handleQuery();
          });
        })
        .catch(() => { });
    },
    handleAdd() {
      if (!this.DepartmentId || this.DepartmentId === 0) {
        this.$message({ message: '请先选择左侧所属机构！', type: 'warning' });
        return;
      }
      this.addFormVisible = true;
      this.addForm = {
        Remark: '',
        DepartmentId: this.DepartmentId,
        FixedAmount: 1,
        UserIds: [],
      };
      this.clearPersonScopeSelection();
    },
    addSubmit() {
      if (!this.personScopeCheckedCount) {
        this.$message({ message: '请先选择人员', type: 'warning' });
        return;
      }
      this.addForm.UserIds = this.collectPersonScopeUserIds();
      this.$refs.addForm.validate((valid) => {
        if (!valid) return;
        this.$confirm('确认提交吗？', '提示', {}).then(() => {
          this.addLoading = true;
          addFixedAmountUserManage(Object.assign({}, this.addForm)).then((res) => {
            this.addLoading = false;
            if (util.isEmt.format(res)) return;
            if (res.data.success) {
              this.$message({ message: res.data.msg, type: 'success' });
              this.$refs.addForm.resetFields();
              this.clearPersonScopeSelection();
              this.addFormVisible = false;
              this.handleQuery();
            } else {
              this.$message({ message: res.data.msg, type: 'error' });
            }
          });
        });
      });
    },

    openPersonScopeDialog() {
      this.personScopeDeptFilter = '';
      this.personScopeNameFilter = '';
      this.personScopeActiveDeptId = '';
      this.personScopeActiveDeptName = '';
      this.personScopeTableRows = [];
      this.personScopePage = 1;
      this.personScopeVisible = true;
      this.loadPersonScopeDirectoryTrees();
      this.$nextTick(() => this.syncPersonScopeTableHeight());
    },
    onPersonScopeDialogClose() {
      this.unbindPersonScopeResize();
      this.personScopeDeptFilter = '';
      this.personScopeNameFilter = '';
      this.personScopeActiveDeptId = '';
      this.personScopeActiveDeptName = '';
      this.personScopeTableRows = [];
      this.personScopePage = 1;
      this.personScopeTableLoading = false;
    },
    confirmPersonScopeSelection() {
      this.addForm.UserIds = this.collectPersonScopeUserIds();
      this.personScopeVisible = false;
    },
    collectPersonScopeUserIds() {
      return Object.keys(this.personScopeSelectedMap || {}).map((k) => {
        const n = this.personScopeSelectedMap[k];
        return [String(n.deptId), String(n.value)];
      });
    },
    removePersonScopeSelected(userId) {
      const nextMap = Object.assign({}, this.personScopeSelectedMap);
      delete nextMap[String(userId)];
      this.personScopeSelectedMap = nextMap;
      this.refreshPersonScopeSummary();
      this.addForm.UserIds = this.collectPersonScopeUserIds();
    },
    normalizePersonScopeTreeRoot(root) {
      if (!root) return [];
      if (Number(root.value || 0) === 0 && root.children && root.children.length) {
        return root.children;
      }
      return [root];
    },
    firstPersonScopeTreeKey(nodes) {
      if (!nodes || !nodes.length) return '';
      return nodes[0] && nodes[0].value != null ? String(nodes[0].value) : '';
    },
    loadPersonScopeDirectoryTrees() {
      this.personScopeTreeLoading = true;
      const scopeId = this.DepartmentId || 0;
      getTeachingDepartmentTree({ pid: 0, scopeDepartmentId: scopeId })
        .then((res) => {
          this.personScopeTeachingTree = this.normalizePersonScopeTreeRoot(
            res && res.data && res.data.response
          );
          const key = this.firstPersonScopeTreeKey(this.personScopeTeachingTree);
          this.personScopeTeachingExpandedKeys = key ? [key] : [];
        })
        .catch(() => {
          this.personScopeTeachingTree = [];
        })
        .finally(() => {
          this.personScopeTreeLoading = false;
          this.$nextTick(() => this.syncPersonScopeTableHeight());
        });
    },
    filterPersonScopeDeptNode(value, data) {
      if (!value) return true;
      return (data.label || '').indexOf(value) !== -1;
    },
    personScopeNodeTone(data) {
      if (!data) return 'default';
      const type = (data.DeptOrgType || data.deptOrgType || '').toLowerCase();
      if (type === 'school' || data.IsCompany || data.isCompany) return 'school';
      if (type === 'campus') return 'campus';
      if (type === 'period' || type === 'stage') return 'period';
      if (type === 'grade') return 'grade';
      if (type === 'class') return 'class';
      return 'default';
    },
    onPersonScopeDeptClick(data) {
      const id = data && (data.value != null ? data.value : data.Id);
      if (id == null || Number(id) <= 0) {
        this.$message.warning('请选择具体部门');
        return;
      }
      this.personScopeActiveDeptId = String(id);
      this.personScopeActiveDeptName = data.label || data.Name || '';
      this.personScopeNameFilter = '';
      this.personScopePage = 1;
      this.loadPersonScopePeople();
    },
    loadPersonScopePeople() {
      const deptId = this.personScopeActiveDeptId;
      if (!deptId) return;
      this.personScopeTableLoading = true;
      this.personScopePage = 1;
      const name = (this.personScopeNameFilter || '').trim();
      getStudentSelectList({ departmentId: deptId, name })
        .then((res) => {
          const list = (res.data && res.data.response) || [];
          this.personScopeTableRows = list.map((s) => ({
            value: String(s.Id),
            label: s.Name || s.StuName || '',
            deptId: String(s.DepartmentId || deptId),
            deptLabel: s.DepartmentName || this.personScopeActiveDeptName,
            scopeDeptId: String(deptId),
            HeadPhotoImage: s.HeadPhotoImage || '',
          }));
          this.$nextTick(() => {
            this.syncPersonScopeTableHeight();
            this.syncPersonScopeTableSelection();
            this.prefetchPersonScopeAvatars();
          });
        })
        .catch(() => {
          this.personScopeTableRows = [];
        })
        .finally(() => {
          this.personScopeTableLoading = false;
        });
    },
    handlePersonScopePageChange(page) {
      // 先锁住 selection-change，避免翻页时空选清掉当页已选。最后更新：2026-10-29
      lockPersonScopeSelectionSync(this);
      this.personScopePage = page;
      this.$nextTick(() => {
        this.syncPersonScopeTableSelection();
        this.prefetchPersonScopeAvatars();
      });
    },
    prefetchPersonScopeAvatars() {
      const rows = this.personScopePagedRows || [];
      if (!rows.length) return;
      this.prefetchListOssMedia(rows, 'HeadPhotoImage');
    },
    syncPersonScopeTableHeight() {
      const measure = () => {
        const wrap = this.$refs.personScopeTableWrap;
        if (!wrap) return;
        const h = Math.floor(wrap.clientHeight);
        if (h > 120 && h !== this.personScopeTableHeight) {
          this.personScopeTableHeight = h;
          this.$nextTick(() => {
            if (this.$refs.personScopeTable && this.$refs.personScopeTable.doLayout) {
              this.$refs.personScopeTable.doLayout();
            }
          });
        }
      };
      this.$nextTick(() => {
        measure();
        setTimeout(measure, 60);
      });
      this.bindPersonScopeResize();
    },
    bindPersonScopeResize() {
      if (this._personScopeResizeBound) return;
      this._personScopeOnResize = () => this.syncPersonScopeTableHeight();
      window.addEventListener('resize', this._personScopeOnResize);
      this._personScopeResizeBound = true;
    },
    unbindPersonScopeResize() {
      if (this._personScopeOnResize) {
        window.removeEventListener('resize', this._personScopeOnResize);
        this._personScopeOnResize = null;
      }
      this._personScopeResizeBound = false;
    },
    syncPersonScopeTableSelection() {
      syncPersonScopeElTableSelection({
        vm: this,
        tableRef: 'personScopeTable',
        rows: this.personScopePagedRows || [],
        isSelected: (row) => !!this.personScopeSelectedMap[row.value],
      });
    },
    onPersonScopeRowClick(row, column, event) {
      if (event && event.target) {
        const target = event.target;
        if (
          target.classList.contains('el-checkbox__original') ||
          target.classList.contains('el-checkbox__inner') ||
          (target.closest && target.closest('.el-checkbox'))
        ) {
          return;
        }
      }
      const table = this.$refs.personScopeTable;
      if (!table || !row) return;
      table.toggleRowSelection(row);
    },
    onPersonScopeTableSelectionChange(selection) {
      if (this.personScopeSyncingTable) return;
      const selectedIds = {};
      (selection || []).forEach((row) => {
        selectedIds[row.value] = true;
      });
      const nextMap = Object.assign({}, this.personScopeSelectedMap);
      (this.personScopePagedRows || []).forEach((row) => {
        if (selectedIds[row.value]) {
          nextMap[row.value] = {
            value: row.value,
            label: row.label,
            deptId: row.deptId,
            deptLabel: row.deptLabel,
            scopeDeptId: row.scopeDeptId,
          };
        } else {
          delete nextMap[row.value];
        }
      });
      this.personScopeSelectedMap = nextMap;
      this.refreshPersonScopeSummary();
    },
    selectAllPersonScopeCurrentDept() {
      const people = this.personScopeTableRows || [];
      if (!people.length) return;
      lockPersonScopeSelectionSync(this);
      const nextMap = Object.assign({}, this.personScopeSelectedMap);
      people.forEach((row) => {
        nextMap[row.value] = {
          value: row.value,
          label: row.label,
          deptId: row.deptId,
          deptLabel: row.deptLabel,
          scopeDeptId: row.scopeDeptId,
        };
      });
      this.personScopeSelectedMap = nextMap;
      this.refreshPersonScopeSummary();
      this.syncPersonScopeTableSelection();
    },
    clearPersonScopeCurrentDept() {
      lockPersonScopeSelectionSync(this);
      const scopeId = String(this.personScopeActiveDeptId || '');
      const nextMap = Object.assign({}, this.personScopeSelectedMap);
      Object.keys(nextMap).forEach((k) => {
        const row = nextMap[k];
        if (String(row.scopeDeptId || row.deptId) === scopeId) delete nextMap[k];
      });
      this.personScopeSelectedMap = nextMap;
      this.refreshPersonScopeSummary();
      this.syncPersonScopeTableSelection();
    },
    clearPersonScopeSelection() {
      lockPersonScopeSelectionSync(this);
      this.personScopeSelectedMap = {};
      this.refreshPersonScopeSummary();
      this.syncPersonScopeTableSelection();
      if (this.addForm) this.addForm.UserIds = [];
    },
    refreshPersonScopeSummary() {
      const list = Object.keys(this.personScopeSelectedMap || {}).map((k) => this.personScopeSelectedMap[k]);
      this.personScopeCheckedCount = list.length;
      const deptSet = {};
      list.forEach((n) => {
        if (n && n.deptId != null) deptSet[String(n.deptId)] = true;
      });
      this.personScopeDeptCount = Object.keys(deptSet).length;
    },
  },
  mounted() {
    const routers = window.localStorage.router
      ? JSON.parse(window.localStorage.router)
      : [];
    this.buttonList = getButtonList(this.$route.path, routers);
  },
};
</script>

<style scoped>
.biz-tip {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0 0 10px;
  padding: 10px 14px;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  color: #0c4a6e;
  font-size: 13px;
  line-height: 1.55;
}
.biz-tip > i {
  margin-top: 2px;
  color: #0284c7;
  flex-shrink: 0;
}
.biz-tip__body em {
  font-style: normal;
  font-weight: 600;
  color: #0369a1;
}
.ps-pick-summary {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  color: #475569;
  font-size: 13px;
}
.ps-pick-tags {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.ps-pick-more {
  color: #94a3b8;
  font-size: 12px;
  line-height: 24px;
}
.ps-layout {
  display: flex;
  height: 640px;
  max-height: calc(90vh - 140px);
  min-height: 420px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}
.ps-left {
  width: 300px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #e2e8f0;
  min-height: 0;
  overflow: hidden;
}
.ps-left-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 14px 8px;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  flex-shrink: 0;
}
.ps-left-header i { color: var(--btn-primary, #2563eb); }
.ps-left-search { padding: 0 12px 10px; flex-shrink: 0; }
.ps-left-tree {
  flex: 1 1 0;
  min-height: 0;
  height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 0 8px 12px;
  position: relative;
}
.ps-left-tree >>> .el-tree-node__content { height: 34px; border-radius: 6px; }
.ps-left-tree >>> .el-tree-node.is-current > .el-tree-node__content { background: #dbeafe; }
.ps-tree-node {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: calc(100% - 24px);
  min-width: 0;
}
.ps-tree-node__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: #94a3b8;
}
.ps-tree-node__dot.tone-school { background: var(--btn-primary, #2563eb); }
.ps-tree-node__dot.tone-campus { background: #f59e0b; }
.ps-tree-node__dot.tone-period { background: #8b5cf6; }
.ps-tree-node__dot.tone-grade { background: #06b6d4; }
.ps-tree-node__dot.tone-class { background: #64748b; }
.ps-tree-node__label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #334155;
  font-size: 13px;
}
.ps-empty {
  position: absolute;
  left: 0;
  right: 0;
  top: 40%;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
}
.ps-right {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 12px 16px 14px;
  overflow: hidden;
}
.ps-alert { margin-bottom: 10px; flex-shrink: 0; }
.ps-meta {
  margin-bottom: 10px;
  color: #475569;
  font-size: 13px;
  flex-shrink: 0;
}
.ps-meta-sep { margin: 0 6px; color: #cbd5e1; }
.ps-filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  flex-shrink: 0;
}
.ps-name-input { width: 180px; }
.ps-hint {
  font-size: 13px;
  color: #475569;
  margin-bottom: 8px;
  flex-shrink: 0;
}
.ps-hint-sub { color: #64748b; margin-left: 4px; }
.ps-hint--warn { color: #b45309; }
.ps-table-wrap {
  flex: 1 1 0;
  min-height: 0;
  height: 0;
  margin-bottom: 8px;
  overflow: hidden;
}
.ps-table-wrap >>> .el-table__body tr { cursor: pointer; }
.ps-avatar-empty { color: #c0c4cc; font-size: 12px; }
.ps-pager-wrap {
  width: 100%;
  margin-bottom: 10px;
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
}
.ps-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
}
.custom-tbl /deep/ .has-gutter .el-checkbox {
  display: none;
}
</style>
